/**
 * [테스트 전용] 계정 목록 — 누르면 그 계정으로 바로 로그인
 * 라우트: /dev/accounts   ⚠️ 정식 오픈 전 제거할 것
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { signInWithCustomToken } from 'firebase/auth';
import { db, auth } from '../../firebase/config';
import { APP_CONFIG } from '../../config/appConfig';
import BrandLoader from '../../components/common/BrandLoader';

const roleTags = (u) => {
  const ms = u.memberships || [];
  const owner = ms.some((m) => ['owner', 'teacher', 'assistant'].includes(m.role));
  const parent = ms.some((m) => m.role === 'parent');
  const tags = [];
  if (owner) tags.push({ label: '학원', color: '#6D28D9', bg: '#EDE9FE' });
  if (parent) tags.push({ label: '학부모', color: '#2E7D32', bg: '#E6F4EA' });
  if (!tags.length) tags.push({ label: u.role || '미가입', color: '#6B7280', bg: '#F2F3F7' });
  return tags;
};

const DevAccountsPage = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');

  useEffect(() => {
    getDocs(collection(db, 'users')).then((snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setUsers(list);
      setLoading(false);
    });
  }, []);

  const loginAs = async (uid) => {
    setBusy(uid);
    try {
      const r = await fetch(APP_CONFIG.devLoginTokenUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid }),
      });
      if (!r.ok) throw new Error('토큰 발급 실패');
      const { customToken } = await r.json();
      await signInWithCustomToken(auth, customToken);
      navigate('/');
    } catch (e) {
      console.error(e);
      alert(`로그인 실패: ${e.message}`);
      setBusy('');
    }
  };

  if (loading) return <BrandLoader fullscreen />;

  const filtered = q
    ? users.filter((u) => (u.name || '').includes(q) || (u.id || '').includes(q))
    : users;

  return (
    <Wrap>
      <Header>
        <Title>테스트 계정 {users.length}개</Title>
        <Sub>계정을 누르면 그 계정으로 바로 로그인돼요.</Sub>
      </Header>
      <Search value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름·계정 검색" />
      <List>
        {filtered.map((u) => (
          <Row key={u.id} type="button" disabled={!!busy} onClick={() => loginAs(u.id)}>
            <Avatar $url={u.photoURL}>
              {!u.photoURL && (u.name || '?').slice(0, 1)}
            </Avatar>
            <Info>
              <NameRow>
                <UName>{u.name || '(이름 없음)'}</UName>
                {roleTags(u).map((t) => (
                  <Tag key={t.label} $c={t.color} $bg={t.bg}>{t.label}</Tag>
                ))}
              </NameRow>
              <Uid>{u.id}</Uid>
              <Provider>{u.provider || u.email || '-'}</Provider>
            </Info>
            {busy === u.id && <Busy>로그인 중…</Busy>}
          </Row>
        ))}
        {filtered.length === 0 && <Empty>계정이 없어요.</Empty>}
      </List>
    </Wrap>
  );
};

export default DevAccountsPage;

const Wrap = styled.div`
  max-width: 480px;
  margin: 0 auto;
  padding: 20px 16px 40px;
  min-height: 100vh;
  background: #fff;
`;

const Header = styled.div`
  margin-bottom: 14px;
`;

const Title = styled.p`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Sub = styled.p`
  margin-top: 5px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.hint};
`;

const Search = styled.input`
  width: 100%;
  padding: 12px 14px;
  margin-bottom: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 10px;
  font-size: 14px;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; outline: none; }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 2px;
  background: none;
  text-align: left;
  border-bottom: 1px solid #F0F1F4;
  &:disabled { opacity: 0.5; }
  &:last-child { border-bottom: none; }
`;

const Avatar = styled.div`
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ $url, theme }) =>
    $url ? `#EDEFF3 url(${$url}) center/cover` : theme.colors.primaryLight};
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const UName = styled.span`
  font-size: 15.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Tag = styled.span`
  padding: 2px 8px;
  border-radius: 7px;
  font-size: 11px;
  font-weight: 700;
  color: ${({ $c }) => $c};
  background: ${({ $bg }) => $bg};
`;

const Uid = styled.p`
  margin-top: 4px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.hint};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Provider = styled.p`
  margin-top: 2px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Busy = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
`;

const Empty = styled.p`
  text-align: center;
  padding: 60px 0;
  font-size: 13.5px;
  color: ${({ theme }) => theme.colors.text.hint};
`;
